import { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Chip,
  CircularProgress,
  Stack,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useTranslation } from 'react-i18next';
import AiOffNote from '../ai/AiOffNote';
import AiDraftedNoteLabel from './AiDraftedNoteLabel';
import { CriterionEvaluation } from '@services/auditService';
import { toDisplayError } from '@utils/apiError';
import { CONFORMANCE_LEVELS, conformanceLevelLabel } from '@utils/conformanceLabels';

/** The levels a person can attest to: every level except not_evaluated. */
const ATTESTABLE_LEVELS = CONFORMANCE_LEVELS.filter((level) => level !== 'not_evaluated');

const ATTESTATION_TIERS = [
  { value: 'audited', label: 'Audited', description: 'High coverage + expert manual + AT testing' },
  { value: 'assessed', label: 'Assessed', description: 'Automated + manual human review' },
  { value: 'sampled', label: 'Sampled', description: 'Representative sample only' },
  { value: 'inferred', label: 'Inferred', description: 'Not directly tested; status inferred' },
];

const LEVEL_COLORS: Record<string, 'success' | 'warning' | 'error' | 'default'> = {
  supports: 'success',
  partially_supports: 'warning',
  does_not_support: 'error',
  not_applicable: 'default',
};

interface AttestationPanelProps {
  criterion: CriterionEvaluation;
  onAttest: (data: {
    level: string;
    tier: string;
    note: string;
    /** REV-1074: the AI draft this note started from, when it did. */
    noteAiCallId?: string;
  }) => Promise<void>;
  /**
   * REV-356: optional handler that asks the platform AI proxy to draft a
   * VPAT remark for this criterion. When provided, a "Suggest narrative
   * (AI)" button appears beside the note field. The reviewer edits the
   * draft before saving — drafting and saving stay separate.
   */
  onGenerateNarrative?: () => Promise<{ narrative: string; callId?: string }>;
  /**
   * REV-1127: the organization has AI switched off. The narrative button is
   * replaced by a note saying so; attesting is unaffected.
   */
  aiOff?: boolean;
  disabled?: boolean;
}

export default function AttestationPanel({
  criterion,
  onAttest,
  onGenerateNarrative,
  aiOff = false,
  disabled = false,
}: AttestationPanelProps) {
  const { t } = useTranslation();
  const [level, setLevel] = useState(criterion.attested_level || '');
  const [tier, setTier] = useState(criterion.attestation_tier || '');
  const [note, setNote] = useState(criterion.attestation_note || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // REV-356: AI narrative draft state. `generating` gates the button,
  // `narrativeNotice` is the inline reminder shown after a draft is
  // inserted, `narrativeError` surfaces proxy/contract failures so the
  // reviewer knows the request didn't silently succeed.
  const [generating, setGenerating] = useState(false);
  const [narrativeNotice, setNarrativeNotice] = useState<string | null>(null);
  const [narrativeError, setNarrativeError] = useState<string | null>(null);
  // REV-1074: the draft the note started from, sent with the attestation so
  // the API can record (and verify) its provenance.
  const [draftCallId, setDraftCallId] = useState<string | null>(null);

  const isOverride = level && level !== criterion.suggested_level;
  const noteRequired = isOverride && !note.trim();

  const handleSubmit = async () => {
    if (!level || !tier) return;
    if (noteRequired) {
      setError(
        t(
          'attestation.noteRequired',
          'A justification note is required when overriding the suggested level.'
        )
      );
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onAttest({ level, tier, note, ...(draftCallId ? { noteAiCallId: draftCallId } : {}) });
      // Saved: the provenance is the criterion's now, and re-attesting the same
      // note without the id keeps it (revenant-api attest()). Clearing it lets
      // the label below show for the note just saved.
      setDraftCallId(null);
    } catch (err) {
      setError(toDisplayError(err, 'Failed to attest'));
    } finally {
      setSaving(false);
    }
  };

  /**
   * Generate an AI narrative draft and overwrite the note field. If the
   * reviewer already typed something, confirm before clobbering — losing
   * a half-written note silently would be worse than a tiny dialog.
   */
  const handleGenerateNarrative = async () => {
    if (!onGenerateNarrative) return;
    if (note.trim().length > 0) {
      const ok = window.confirm(
        t(
          'attestation.narrative.confirmReplace',
          'Replace your current note with an AI-drafted one? Your existing text will be discarded.'
        )
      );
      if (!ok) return;
    }
    setGenerating(true);
    setNarrativeError(null);
    setNarrativeNotice(null);
    try {
      const result = await onGenerateNarrative();
      setNote(result.narrative);
      setDraftCallId(result.callId ?? null);
      setNarrativeNotice(
        t(
          'attestation.narrative.draftInserted',
          'AI-drafted narrative inserted. Review carefully and edit before submitting your attestation.'
        )
      );
    } catch (err) {
      setNarrativeError(
        toDisplayError(err, t('attestation.narrative.error', 'Failed to generate narrative draft.'))
      );
    } finally {
      setGenerating(false);
    }
  };

  const alreadyAttested = !!criterion.attested_level;

  return (
    <Paper
      sx={{
        p: 3,
        mb: 3,
        border: '2px solid',
        borderColor: alreadyAttested ? 'success.light' : 'primary.light',
      }}
    >
      <Typography variant="h6" gutterBottom>
        {alreadyAttested
          ? t('attestation.updateTitle', 'Update Attestation')
          : t('attestation.title', 'Attest This Criterion')}
      </Typography>

      {alreadyAttested && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="success" variant="outlined">
            {t('attestation.currentlyAttested', 'Currently attested as')}{' '}
            <Chip
              label={conformanceLevelLabel(t, criterion.attested_level ?? '')}
              color={LEVEL_COLORS[criterion.attested_level || ''] || 'default'}
              size="small"
            />{' '}
            ({t('attestation.tier', 'Tier')}: {criterion.attestation_tier})
          </Alert>
        </Box>
      )}

      {criterion.suggested_level && criterion.suggested_level !== 'not_evaluated' && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {t('attestation.suggestedLevel', 'Rollup suggests')}:{' '}
            <strong>{conformanceLevelLabel(t, criterion.suggested_level)}</strong>
          </Typography>
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel id="attestation-conformance-level-label">
            {t('attestation.conformanceLevel', 'Conformance Level')}
          </InputLabel>
          <Select
            labelId="attestation-conformance-level-label"
            value={level}
            label={t('attestation.conformanceLevel', 'Conformance Level')}
            onChange={(e) => {
              setLevel(e.target.value);
              setError(null);
            }}
            disabled={disabled}
          >
            {ATTESTABLE_LEVELS.map((value) => (
              <MenuItem key={value} value={value}>
                {value === criterion.suggested_level
                  ? t('attestation.levelSuggested', '{{level}} (suggested)', {
                      level: conformanceLevelLabel(t, value),
                    })
                  : conformanceLevelLabel(t, value)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel id="attestation-confidence-tier-label">
            {t('attestation.confidenceTier', 'Confidence Tier')}
          </InputLabel>
          <Select
            labelId="attestation-confidence-tier-label"
            value={tier}
            label={t('attestation.confidenceTier', 'Confidence Tier')}
            onChange={(e) => setTier(e.target.value)}
            disabled={disabled}
          >
            {ATTESTATION_TIERS.map((t) => (
              <MenuItem key={t.value} value={t.value}>
                {t.label} — {t.description}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <TextField
        fullWidth
        multiline
        minRows={2}
        maxRows={6}
        size="small"
        label={
          isOverride
            ? t(
                'attestation.justificationRequired',
                'Justification (required — overriding suggestion)'
              )
            : t('attestation.note', 'Attestation note')
        }
        value={note}
        onChange={(e) => {
          setNote(e.target.value);
          if (error && e.target.value.trim()) setError(null);
        }}
        error={!!noteRequired && !!error}
        helperText={
          isOverride
            ? t(
                'attestation.overrideHelper',
                'Explain why the attested level differs from the rollup suggestion.'
              )
            : ''
        }
        disabled={disabled}
        sx={{ mb: 1 }}
      />
      {/* REV-1074: a saved note that started as an AI draft says so. */}
      {!draftCallId && note === (criterion.attestation_note || '') && (
        <AiDraftedNoteLabel criterion={criterion} />
      )}

      {/* REV-356: AI draft outcome banner — distinct from the attestation
          submit error so a transient narrative failure doesn't suggest the
          attestation itself failed. */}
      {narrativeNotice && (
        <Alert
          severity="info"
          variant="outlined"
          sx={{ mb: 2 }}
          onClose={() => setNarrativeNotice(null)}
        >
          {narrativeNotice}
        </Alert>
      )}
      {narrativeError && (
        <Alert
          severity="warning"
          variant="outlined"
          sx={{ mb: 2 }}
          onClose={() => setNarrativeError(null)}
        >
          {narrativeError}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={disabled || saving || !level || !tier}
        >
          {saving ? (
            <CircularProgress size={20} />
          ) : alreadyAttested ? (
            t('attestation.update', 'Update Attestation')
          ) : (
            t('attestation.submit', 'Submit Attestation')
          )}
        </Button>

        {onGenerateNarrative && !aiOff && (
          <Button
            variant="outlined"
            startIcon={
              generating ? <CircularProgress size={16} /> : <AutoAwesomeIcon fontSize="small" />
            }
            onClick={handleGenerateNarrative}
            disabled={disabled || generating}
          >
            {generating
              ? t('attestation.narrative.generating', 'Drafting…')
              : t('attestation.narrative.button', 'Suggest narrative (AI)')}
          </Button>
        )}
      </Stack>
      {onGenerateNarrative && aiOff && <AiOffNote sx={{ mt: 2 }} />}
    </Paper>
  );
}
